/**
 * This should only be used by user-profile-provider.js.
 */
import gql from "graphql-tag";

import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import Log, {Errors, KAError} from "@khan/logging";

import ProfileModel, {
    type ProfileModelType,
} from "#shared-package/profile-model.ts";

import type {
    getFullUserProfile,
    getFullUserProfileVariables,
} from "./__graphql-types__/getFullUserProfile.ts";

// NOTE: Not all fields are defined in query result type. Only those fields
// that are transformed to other profile model props are defined, all other
// fields are passthrough.
export type ProfileData = getFullUserProfile;

// NOTE: sending neither $kaid nor $username will result in the current
// user's profile being queried.
export const userProfileQuery: GqlOperation<
    getFullUserProfile,
    getFullUserProfileVariables
> = gqlOp(gql`
    query getFullUserProfile($kaid: String, $username: String) {
        user(kaid: $kaid, username: $username) {
            id
            kaid
            key
            userId
            email
            username
            profileRoot
            gaUserId
            isPhantom
            isDeveloper: hasPermission(name: "can_do_what_only_admins_can_do")
            isPublisher: hasPermission(
                name: "can_publish"
                scope: ANY_ON_CURRENT_LOCALE
            )
            isModerator: hasPermission(
                name: "can_moderate_users"
                scope: GLOBAL
            )
            isParent
            isTeacher
            isFormalTeacher
            isK4dStudent
            isKmapStudent
            isDataCollectible
            isChild
            isOrphan
            isCoachingLoggedInUser
            canModifyCoaches
            nickname
            hideVisual
            joined
            points
            countVideosCompleted
            bio
            profile {
                accessLevel
            }
            soundOn
            muteVideos
            showCaptions
            prefersReducedMotion
            noColorInVideos
            newNotificationCount
            canHellban: hasPermission(name: "can_ban_users", scope: GLOBAL)
            canMessageUsers: hasPermission(
                name: "can_send_moderator_messages"
                scope: GLOBAL
            )
            isSelf: isActor
            hasStudents: hasCoachees
            hasClasses
            hasChildren
            hasCoach
            badgeCounts
            homepageUrl
            isMidsignupPhantom
            includesDistrictOwnedData
            includesKmapDistrictOwnedData
            includesK4dDistrictOwnedData
            canAccessDistrictsHomepage
            underAgeGate {
                parentEmail
                daysUntilCutoff
                approvalGivenAt
            }
            authEmails
            signupDataIfUnverified {
                email
                emailBounced
            }
            pendingEmailVerifications {
                email
            }
            hasAccessToAIGuideCompanionMode
            hasAccessToAIGuideLearner
            hasAccessToAIGuideDistrictAdmin
            hasAccessToAIGuideParent
            hasAccessToAIGuideTeacher
            tosAccepted
            shouldShowAgeCheck
            birthMonthYear
            lastLoginCountry
            region
            userDistrictInfos {
                id
                isKAD
                district {
                    id
                    region
                }
            }
            schoolAffiliation {
                id
                location
            }
        }
        actorIsImpersonatingUser
        isAIGuideEnabled
        hasAccessToAIGuideDev
    }
`);

// TODO(FEI-3298): Update `ProfileModelType` to consume fields directly from the
// gqlflow generated `getFullUserProfile`.
export const profileFromQueryData = ({
    user,
    ...otherFields
}: ProfileData): ProfileModelType => {
    if (!user) {
        // Log an error.
        Log.error(
            "No user data from GraphQL query for current user",
            Errors.Internal,
        );

        // Throw since anything we would show to the user would
        // be incorrect at this point anyway.  This should result
        // in a rejected promise.
        throw new KAError("Couldn't retrieve user profile", Errors.Internal);
    }

    // If we recieve an accessLevel of PRIVATE, translate it to COACH because
    // PRIVATE is not a valid accessLevel.
    // @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"COACH" | "PUBLIC" | null | undefined' and '"PRIVATE"' have no overlap.
    if (user.profile?.accessLevel === "PRIVATE") {
        // PRIVATE is not a valid accessLevel. This should already be handled
        // by the backend, but we do this check on the Frontend as
        // an added precaution.
        user.profile.accessLevel = "COACH";
    }

    // sync-start:profile-model-mapping 541803689 ./apps/khanacademy/src/data-access/user-profile-provider.tsx
    const data = {
        ...otherFields,
        ...user,
        dateJoined: user.joined,
        countBrandNewNotifications: user.newNotificationCount,
        hasChildStudents: user.hasChildren,
        isChildAccount: user.isChild,
        // A mapping of badge categories (number) to count as JSON.
        // @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
        badgeCounts: JSON.parse(user.badgeCounts ?? null),
        // Indicate that full profile information is available.
        includesUserDataInfo: true,
        // Having a set `id` field indicates an existing model,
        // causing Backbone to PUT instead of POST when saving profile.
        id: undefined,
    } as const;
    // sync-end:profile-model-mapping

    return new ProfileModel(data);
};

export const fetchProfileData = (
    gqlClient: ApolloGqlClient,
    variables: getFullUserProfileVariables,
): Promise<ProfileData> => {
    return gqlClient
        .query(userProfileQuery, {
            variables,
            fetchPolicy: "network-only",
        })
        .then(({data}) => {
            if (!data?.user) {
                // Log an error.
                Log.error(
                    "No user data from GraphQL query for current user",
                    Errors.Internal,
                );

                // Throw since anything we would show to the user would
                // be incorrect at this point anyway.  This should result
                // in a rejected promise.
                throw new KAError(
                    "Couldn't retrieve user profile",
                    Errors.Internal,
                );
            }

            return data;
        });
};

export const fetchProfile = (
    gqlClient: ApolloGqlClient,
    variables: getFullUserProfileVariables,
): Promise<ProfileModelType> =>
    fetchProfileData(gqlClient, variables).then(profileFromQueryData);
